// Reduced-motion helpers.
//
// Centralised here so every scroll site in the app makes the same call
// instead of each component re-implementing the matchMedia check (and
// some forgetting it). Two helpers:
//
//   - prefersReducedMotion(): boolean — reads the user's OS-level
//     `prefers-reduced-motion` setting. SSR-safe (returns `false`).
//   - motionScrollBehavior(): "smooth" | "auto" — drop-in value for the
//     `behavior` field of `scrollIntoView` / `scrollTo` calls. `"auto"`
//     means instant, which is what reduced-motion users expect.
//
// We intentionally read the media query on every call rather than
// caching: users can flip the setting at runtime (especially on
// macOS/Windows accessibility panels) and our scroll code should pick
// that up without a reload.

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    // User-level override (set by the playbook reading-mode menu, etc.).
    // Tri-state:
    //   "1" → explicit opt-in  (force reduce, even if OS says no-pref)
    //   "0" → explicit opt-out (force motion, even if OS says reduce)
    //   missing → follow the OS `prefers-reduced-motion` query
    // Lets motion-sensitive visitors get a calm UI for free while still
    // letting anyone override the OS pref for shared machines.
    try {
      const v = window.localStorage.getItem("hl.reduceMotion");
      if (v === "1") return true;
      if (v === "0") return false;
    } catch {
      /* storage unavailable — fall through to OS pref */
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function motionScrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? "auto" : "smooth";
}